import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  signal,
  WritableSignal,
} from '@angular/core';
import { DocumentDto, HttpError } from '@myarvato/shared/models';
import { Button } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';
import { UserTrackingService } from '@myarvato/core/services';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommonModule } from '@angular/common';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { MultiUnitSelectionComponent } from './components/multi-unit-selection/multi-unit-selection.component';
import { FileDropZoneDirective } from './components/file-drop-zone.directive';
import { FloatLabel } from 'primeng/floatlabel';
import { ExpandableInfoTextComponent } from './components/expandable-info-text/expandable-info-text.component';
import { SelectFileComponent } from './components/select-file/select-file.component';
import { ErrorMessageComponent, PrimeNGError } from '@myarvato/components/message';
import { validateFile } from './file-validation.utils';
import { DocumentsService } from '@myarvato/modules/transport/services';
import { firstValueFrom } from 'rxjs';
import { CustomizationTranslationService } from '@myarvato/shared/services';
import { HandlingUnit } from '@myarvato/modules/transport/models';

@Component({
  selector: 'transport-upload-document-dialog',
  imports: [
    CommonModule,
    Button,
    TranslatePipe,
    MultiUnitSelectionComponent,
    Select,
    FormsModule,
    MultiUnitSelectionComponent,
    FileDropZoneDirective,
    FloatLabel,
    ExpandableInfoTextComponent,
    SelectFileComponent,
    ErrorMessageComponent,
  ],
  templateUrl: './upload-document-dialog.component.html',
  styleUrls: ['./upload-document-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadDocumentDialogComponent {
  // constants
  private readonly maxFileSize = 25 * 1024 * 1024; // 25 MB
  private readonly allowedFileTypes = ['application/pdf', 'image/jpeg'];
  private readonly categoryTracker: string = 'Upload Document Dialog';

  // inputs
  public readonly allowedDocumentTypes = input<string[]>([]);
  public readonly existingDocuments = input<DocumentDto[]>([]);
  public readonly handlingUnits = input<HandlingUnit[]>();
  public readonly initialHandlingUnit = input.required<HandlingUnit>();

  // change detection
  protected readonly isFormValid = computed(() => {
    const selectedDocumentType = this.selectedDocumentType();
    if (!selectedDocumentType) {
      return false;
    }

    const availableHandlingUnits = this.handlingUnits();
    const selectedHandlingUnits = this.selectedHandlingUnits();
    if (!this.isHandlingUnitValid(availableHandlingUnits, selectedHandlingUnits)) {
      return false;
    }

    return this.selectedFile() != null;
  });

  protected readonly errorMessage: WritableSignal<PrimeNGError | undefined> = signal<PrimeNGError | undefined>(undefined);
  protected readonly selectedDocumentType = model<string>();
  protected readonly selectedFile = signal<File | undefined>(undefined);
  protected readonly selectedHandlingUnits = linkedSignal<HandlingUnit[]>(() => [this.initialHandlingUnit()]);

  // services
  private readonly customizationTranslationService = inject(CustomizationTranslationService);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly documentsService = inject(DocumentsService);
  private readonly tracker = inject(UserTrackingService);

  constructor() {
    // check if the form is valid
    effect(() => {
      const selectedFile = this.selectedFile();
      const message = this.getValidationMessage(selectedFile);
      this.errorMessage.set(message);
    });
  }

  protected cancel() {
    this.tracker.trackEvent(this.categoryTracker, 'Cancel');
    this.dialogRef.close();
  }

  protected async uploadFile() {
    if (!this.isFormValid()) {
      return;
    }

    try {
      const result = await firstValueFrom(
        this.documentsService.uploadDocument({
          file: this.selectedFile()!,
          documentType: this.selectedDocumentType()!,
          handlingUnits: this.getSelectedHandlingUnit()!,
        }),
      );

      if (result) {
        this.tracker.trackEvent(this.categoryTracker, 'Upload');
        this.dialogRef.close(true);
      }
    } catch (err) {
      console.log(err);
      const error = err as HttpError;

      this.errorMessage.set({
        summary: this.customizationTranslationService.getTranslation(`errors.${error.error.errorCode}.title`, 'errors.G0400.title'),
        detail: this.customizationTranslationService.getTranslation(
          `errors.${error.error.errorCode}.description`,
          'errors.G0400.description',
        ),
      });
    }
  }

  private getSelectedHandlingUnit(): HandlingUnit[] | undefined {
    // if we are handed zero to one handling units, the selection is hidden, so we just return those.
    const availableHandlingUnits = this.handlingUnits();
    if (availableHandlingUnits == null) {
      return undefined;
    }

    if (availableHandlingUnits.length === 1) {
      return [availableHandlingUnits[0]];
    }

    return this.selectedHandlingUnits();
  }

  private isHandlingUnitValid(availableHandlingUnits: HandlingUnit[] | undefined, selectedHandlingUnits: HandlingUnit[]): boolean {
    if (availableHandlingUnits == null || availableHandlingUnits.length <= 1) {
      // no handling units given or max. 1 selected? -> valid
      return true;
    }

    return selectedHandlingUnits.length > 0;
  }

  private getValidationMessage(selectedFile: File | undefined): PrimeNGError | undefined {
    if (!selectedFile) {
      return undefined;
    }

    const validation = validateFile(selectedFile, this.allowedFileTypes, this.maxFileSize);
    if (validation.success) {
      return undefined;
    }

    return {
      summary: 'pages.transport.upload-dialog.errors.error-summary',
      detail: validation.message,
    };
  }
}
